import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Settings, ArrowLeft, Plus, Trash2, Save } from 'lucide-react'
import { tournamentsAPI } from '@/lib/api'
import PageHeader from '@/components/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const TYPES = ['single_elimination','double_elimination','round_robin','deathmatch']
const REGS  = ['personal','team']
const SCORES = ['kills','points']

export default function TournamentTemplatesAdminPage() {
  const [list, setList] = useState([])
  const [draft, setDraft] = useState(null)   // {id?, name, ...}
  const [saving, setSaving] = useState(false)

  const load = () => tournamentsAPI.listTemplates().then(r => setList(r.data.templates || []))
  useEffect(() => { load() }, [])

  const save = async () => {
    setSaving(true)
    try {
      if (draft.id) await tournamentsAPI.updateTemplate(draft.id, draft)
      else await tournamentsAPI.createTemplate(draft)
      setDraft(null)
      await load()
    } catch (e) { alert('Fout: ' + (e.response?.data?.error || e.message)) }
    finally { setSaving(false) }
  }

  const remove = async (t) => {
    if (!confirm(`Template "${t.name}" verwijderen?`)) return
    await tournamentsAPI.deleteTemplate(t.id)
    await load()
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Settings}
        eyebrow="Beheer · tournaments"
        title="Templates"
        subtitle="Voor het Snel-opzetten menu. Nieuwe presets aanmaken of bestaande aanpassen."
        actions={
          <>
            <Button onClick={() => setDraft({ name:'', description:'', game:'', type:'single_elimination', scoring_type:'kills', registration_type:'personal', max_participants:8, rounds:1, features:{}, points_reward:{} })}>
              <Plus className="h-4 w-4 mr-2" />Nieuwe template
            </Button>
            <Button asChild variant="outline"><Link to="/tournaments"><ArrowLeft className="h-4 w-4 mr-2" />Terug</Link></Button>
          </>
        }
      />

      {draft && <DraftEditor draft={draft} setDraft={setDraft} save={save} saving={saving} onCancel={() => setDraft(null)} />}

      <Card>
        <CardContent className="p-0 divide-y divide-border/40">
          {list.map(t => (
            <div key={t.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-4 py-3">
              <div>
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-muted-foreground truncate">{t.description}</div>
                <div className="font-mono text-[10px] text-muted-foreground mt-1">
                  {t.game || 'geen game'} · {t.type} · {t.registration_type} · {t.scoring_type}
                  {t.max_participants ? ` · max ${t.max_participants}` : ''}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...t, features: t.features || {}, points_reward: t.points_reward || {} })}>Aanpassen</Button>
              <Button size="sm" variant="outline" onClick={() => remove(t)}>
                <Trash2 className="h-4 w-4 text-signal" />
              </Button>
              <div />
            </div>
          ))}
          {list.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">Geen templates. Voeg er één toe.</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function DraftEditor({ draft, setDraft, save, saving, onCancel }) {
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))
  const setFeat = (k, v) => setDraft(d => ({ ...d, features: { ...(d.features || {}), [k]: v } }))
  const setPts  = (k, v) => setDraft(d => ({ ...d, points_reward: { ...(d.points_reward || {}), [k]: Number(v) || 0 } }))

  return (
    <Card className="border-acid/40">
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <TxtField label="Naam" value={draft.name} onChange={v => set('name', v)} />
          <TxtField label="Game" value={draft.game || ''} onChange={v => set('game', v)} />
          <TxtField label="Beschrijving" value={draft.description || ''} onChange={v => set('description', v)} full />
          <SelField label="Type" value={draft.type} onChange={v => set('type', v)} options={TYPES} />
          <SelField label="Registratie" value={draft.registration_type} onChange={v => set('registration_type', v)} options={REGS} />
          <SelField label="Scoring" value={draft.scoring_type} onChange={v => set('scoring_type', v)} options={SCORES} />
          <NumField label="Max deelnemers" value={draft.max_participants || ''} onChange={v => set('max_participants', v ? Number(v) : null)} />
          <NumField label="Rondes" value={draft.rounds || 1} onChange={v => set('rounds', Number(v) || 1)} />
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Features aan/uit</div>
          <div className="grid grid-cols-3 gap-2">
            {['check_in','ready_check','beamer','predictions','auto_prize','points_award','badges','auto_score_cs'].map(k => (
              <label key={k} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!draft.features?.[k]} onChange={e => setFeat(k, e.target.checked)} />
                {k}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Punten per plaats</div>
          <div className="grid grid-cols-4 gap-2">
            {['1','2','3','default'].map(k => (
              <label key={k} className="flex flex-col gap-1">
                <span className="font-mono text-[10px] uppercase text-muted-foreground">{k}</span>
                <input type="number" min={0} value={draft.points_reward?.[k] || 0}
                       onChange={e => setPts(k, e.target.value)}
                       className="h-8 rounded-[4px] border border-border bg-background px-2 text-sm text-right tabular-nums" />
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border/40 pt-3">
          <Button variant="outline" onClick={onCancel}>Annuleren</Button>
          <Button onClick={save} disabled={saving || !draft.name}>
            <Save className="h-4 w-4 mr-2" />Opslaan
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function TxtField({ label, value, onChange, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? 'col-span-2' : ''}`}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <input value={value} onChange={e => onChange(e.target.value)}
             className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm" />
    </label>
  )
}
function NumField({ label, value, onChange }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <input type="number" min={0} value={value} onChange={e => onChange(e.target.value)}
             className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm tabular-nums" />
    </label>
  )
}
function SelField({ label, value, onChange, options }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)}
              className="h-9 rounded-[4px] border border-border bg-background px-3 text-sm">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  )
}
